import { createApiRef } from '@backstage/core-plugin-api';

/**
 * A persisted sync-selection row as returned by the backend.
 */
export interface SyncSelection {
  id: string;
  entity_ref: string;
  kind: string;
  entity_name: string;
  entity_title: string | null;
  gitlab_slug: string | null;
  selected_by: string | null;
  /**
   * The IMC metric profile-key assigned to this repo (feature 0017), or `null`
   * when the repo is synced but unprofiled. IMC owns the vocabulary; this is
   * stored/echoed verbatim.
   */
  profile: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Payload for marking an entity for sync.
 */
export interface AddSelectionRequest {
  entityRef: string;
  kind: string;
  entityName: string;
  entityTitle?: string | null;
  gitlabSlug?: string | null;
  selectedBy?: string | null;
  /**
   * The chosen metric profile-key (feature 0017). Omit to leave any existing
   * profile untouched (e.g. a plain sync toggle); `null` clears it.
   */
  profile?: string | null;
}

/**
 * One platform-defined metric profile from IMC's catalog
 * (`GET /api/imc/v1/profiles.json`, feature 0017). IMC is the authority on the
 * vocabulary; Backstage only renders these and stores the chosen `key`.
 */
export interface MetricProfile {
  key: string;
  title: string;
  description: string;
  applicableMetricKeys: string[];
}

/**
 * The excluded (unchecked) AWS account ids for one System — the deviations from
 * the default all-included curation (feature 0011).
 */
export interface AwsAccountExclusions {
  systemRef: string;
  excludedAccountIds: string[];
}

export interface ImcSyncApi {
  getSelections(kind?: string): Promise<SyncSelection[]>;
  addSelection(request: AddSelectionRequest): Promise<SyncSelection>;
  removeSelection(entityRef: string): Promise<void>;

  /**
   * The platform-defined metric-profile catalog, fetched from IMC's
   * `profiles.json` (config `imcSync.profilesUrl`, feature 0017). Returns `[]`
   * when no URL is configured so the picker can degrade to disabled.
   */
  getProfiles(): Promise<MetricProfile[]>;

  /** The account ids currently excluded (unchecked) for the given System. */
  getAwsAccountExclusions(systemRef: string): Promise<string[]>;
  /** Full-replace the excluded account ids for the given System. */
  setAwsAccountExclusions(
    systemRef: string,
    excludedAccountIds: string[],
  ): Promise<void>;
}

export const imcSyncApiRef = createApiRef<ImcSyncApi>({
  id: 'plugin.imc-sync.api',
});
